import React, { createContext, ReactNode, useContext, useReducer } from "react";

interface State {
  popupDeletePlaylistItemAlert: boolean;
  showingPlaylistPanel: boolean;
  deletePlaylistModal: boolean;
  selectedPlaylistId: Record<string, any>;
}

const initialState: State = {
  popupDeletePlaylistItemAlert: false,
  showingPlaylistPanel: false,
  deletePlaylistModal: false,
  selectedPlaylistId: {},
};

type Action =
  | { type: "SET_POPUP_DELETE_ALERT"; payload: boolean }
  | { type: "SET_SHOWING_PLAYLIST_PANEL"; payload: boolean }
  | { type: "SET_DELETE_PLAYLIST_MODAL"; payload: boolean }
  | { type: "SET_SELECTED_PLAYLIST_ID"; payload: Record<string, any> };

const actions = {
  SET_POPUP_DELETE_ALERT: "SET_POPUP_DELETE_ALERT" as const,
  SET_SHOWING_PLAYLIST_PANEL: "SET_SHOWING_PLAYLIST_PANEL" as const,
  SET_DELETE_PLAYLIST_MODAL: "SET_DELETE_PLAYLIST_MODAL" as const,
  SET_SELECTED_PLAYLIST_ID: "SET_SELECTED_PLAYLIST_ID" as const,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actions.SET_POPUP_DELETE_ALERT:
      return { ...state, popupDeletePlaylistItemAlert: action.payload };
    case actions.SET_SHOWING_PLAYLIST_PANEL:
      return { ...state, showingPlaylistPanel: action.payload };
    case actions.SET_DELETE_PLAYLIST_MODAL:
      return { ...state, deletePlaylistModal: action.payload };
    case actions.SET_SELECTED_PLAYLIST_ID:
      return { ...state, selectedPlaylistId: action.payload };
    default:
      return state;
  }
};

interface PlaylistsAPI {
  setPopupDeletePlaylistItemAlert: (value: boolean) => void;
  setShowingPlaylistPanel: (value: boolean) => void;
  setDeletePlaylistModal: (value: boolean) => void;
  setSelectedPlaylistId: (value: Record<string, any>) => void;
}

const PlaylistsStateContext = createContext<State | undefined>(undefined);
const PlaylistsAPIContext = createContext<PlaylistsAPI | undefined>(undefined);

export const PlaylistsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const api: PlaylistsAPI = {
    setPopupDeletePlaylistItemAlert: value => dispatch({ type: actions.SET_POPUP_DELETE_ALERT, payload: value }),
    setShowingPlaylistPanel: value => dispatch({ type: actions.SET_SHOWING_PLAYLIST_PANEL, payload: value }),
    setDeletePlaylistModal: value => dispatch({ type: actions.SET_DELETE_PLAYLIST_MODAL, payload: value }),
    setSelectedPlaylistId: value => dispatch({ type: actions.SET_SELECTED_PLAYLIST_ID, payload: value }),
  };

  return (
    <PlaylistsAPIContext.Provider value={api}>
      <PlaylistsStateContext.Provider value={state}>{children}</PlaylistsStateContext.Provider>
    </PlaylistsAPIContext.Provider>
  );
};

export const usePlaylistsStateContext = (): State => {
  const context = useContext(PlaylistsStateContext);
  if (!context) {
    throw new Error("usePlaylistsStateContext must be used within a PlaylistsProvider");
  }
  return context;
};

export const usePlaylistsAPIContext = () => {
  const context = useContext(PlaylistsAPIContext);
  if (!context) {
    throw new Error("usePlaylistsAPIContext must be used within a PlaylistsProvider");
  }
  return context;
};
